// src/components/inputs/TimeInput.jsx

import React, { useState, useRef, useEffect } from "react";
import {
    TextField,
    InputAdornment,
    IconButton,
    Popover,
    Box,
    Typography,
    Button,
    Grid,
    useTheme,
    alpha,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import AccessTimeIcon from "@mui/icons-material/AccessTime";

/**
 * Custom 24-Hour Time Picker Component
 * Matches FormInput styling exactly
 */
const TimeInput = ({
    label,
    value = "",
    onChange,
    required = false,
    disabled = false,
    error = false,
    helperText = "",
    fullWidth = true,
    ...rest
}) => {
    const { i18n } = useTranslation();
    const lng = i18n.language || "en";
    const isArabic = lng === "ar";
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";

    const [anchorEl, setAnchorEl] = useState(null);
    const [tempHour, setTempHour] = useState("00");
    const [tempMinute, setTempMinute] = useState("00");
    const inputRef = useRef(null);

    // Parse current value
    useEffect(() => {
        if (value) {
            const [h, m] = value.split(":");
            setTempHour(h || "00");
            setTempMinute(m || "00");
        }
    }, [value]);

    const handleOpenPicker = (event) => {
        if (!disabled) {
            setAnchorEl(event.currentTarget);
        }
    };

    const handleClosePicker = () => {
        setAnchorEl(null);
    };

    const handleApply = () => {
        const newValue = `${tempHour}:${tempMinute}`;
        onChange({ target: { value: newValue } });
        handleClosePicker();
    };

    const handleInputChange = (e) => {
        const inputValue = e.target.value;

        // السماح بالإدخال اليدوي بصيغة HH:MM
        const timeRegex = /^([0-1]?[0-9]|2[0-3]):?([0-5]?[0-9])?$/;
        if (timeRegex.test(inputValue) || inputValue === '') {
            onChange(e);
        }
    };

    const handleInputBlur = (e) => {
        let inputValue = e.target.value;

        if (inputValue) {
            inputValue = inputValue.replace(/[^\d:]/g, '');
            const parts = inputValue.split(':');
            let hours = parts[0] ? parseInt(parts[0], 10) : 0;
            let minutes = parts[1] ? parseInt(parts[1], 10) : 0;

            hours = Math.min(23, Math.max(0, hours));
            minutes = Math.min(59, Math.max(0, minutes));

            const formattedTime = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;

            if (formattedTime !== value) {
                onChange({ target: { value: formattedTime } });
            }
        }
    };

    const open = Boolean(anchorEl);

    // Generate hours (00-23)
    const hours = Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, "0"));
    // Generate minutes (00-59) with 5-minute intervals
    const minutes = Array.from({ length: 12 }, (_, i) => (i * 5).toString().padStart(2, "0"));

    return (
        <>
            <TextField
                ref={inputRef}
                fullWidth={fullWidth}
                className="input"
                type="text"
                label={label}
                value={value}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                onClick={handleOpenPicker}
                placeholder={isArabic ? "00:00" : "HH:MM"}
                required={required}
                disabled={disabled}
                error={error}
                helperText={helperText}
                InputLabelProps={{
                    shrink: true,
                }}
                InputProps={{
                    readOnly: false,
                    endAdornment: (
                        <InputAdornment position="end">
                            <IconButton
                                edge="end"
                                onClick={handleOpenPicker}
                                disabled={disabled}
                                size="small"
                                sx={{
                                    mr: -0.5,
                                }}
                            >
                                <AccessTimeIcon fontSize="small" />
                            </IconButton>
                        </InputAdornment>
                    ),
                }}
                sx={{
                    direction: isArabic ? "rtl" : "ltr",
                    "& .MuiInputLabel-root": {
                        left: isArabic ? "auto" : undefined,
                        right: isArabic ? "1.75rem" : undefined,
                        transformOrigin: isArabic ? "right" : "left",
                    },
                    "& .MuiOutlinedInput-root": {
                        backgroundColor: isDark 
                            ? alpha(theme.palette.common.white, 0.05) 
                            : alpha(theme.palette.common.black, 0.02),
                        cursor: "pointer",
                        // Match FormInput height exactly
                        "& input": {
                            height: "1.4375em", // Default MUI height
                            padding: "16.5px 14px", // Match MUI default padding
                            fontFamily: theme.typography.fontFamily,
                            fontSize: "1rem",
                            letterSpacing: "normal",
                        },
                        "&:hover": {
                            backgroundColor: isDark 
                                ? alpha(theme.palette.common.white, 0.08) 
                                : alpha(theme.palette.common.black, 0.04),
                        },
                        "&.Mui-focused": {
                            backgroundColor: "transparent",
                        },
                    },
                    // Remove autofill background
                    "& input:-webkit-autofill": {
                        WebkitBoxShadow: `0 0 0 100px ${
                            isDark ? theme.palette.background.paper : "#fff"
                        } inset`,
                        WebkitTextFillColor: theme.palette.text.primary,
                    },
                }}
                {...rest}
            />

            {/* Custom Time Picker Popover */}
            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClosePicker}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: isArabic ? "right" : "left",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: isArabic ? "right" : "left",
                }}
                PaperProps={{
                    sx: {
                        p: 2.5,
                        minWidth: 300,
                        maxHeight: 450,
                        borderRadius: 2,
                        boxShadow: theme.shadows[8],
                    },
                }}
            >
                <Box>
                    <Typography 
                        variant="subtitle2" 
                        sx={{ 
                            mb: 2, 
                            fontWeight: 700,
                            color: "primary.main",
                        }}
                    >
                        {isArabic ? "⏰ اختر الوقت (24 ساعة)" : "⏰ Select Time (24-hour)"}
                    </Typography>

                    <Grid container spacing={2}>
                        {/* Hours Column */}
                        <Grid item xs={6}>
                            <Typography 
                                variant="caption" 
                                sx={{ 
                                    mb: 1, 
                                    display: "block", 
                                    fontWeight: 600,
                                    color: "text.secondary",
                                }}
                            >
                                {isArabic ? "الساعة" : "Hour"}
                            </Typography>
                            <Box
                                sx={{
                                    maxHeight: 220,
                                    overflowY: "auto",
                                    border: `1px solid ${theme.palette.divider}`,
                                    borderRadius: 1.5,
                                    "&::-webkit-scrollbar": {
                                        width: "8px",
                                    },
                                    "&::-webkit-scrollbar-track": {
                                        background: alpha(theme.palette.divider, 0.1),
                                        borderRadius: "4px",
                                    },
                                    "&::-webkit-scrollbar-thumb": {
                                        backgroundColor: alpha(theme.palette.primary.main, 0.5),
                                        borderRadius: "4px",
                                        "&:hover": {
                                            backgroundColor: theme.palette.primary.main,
                                        },
                                    },
                                }}
                            >
                                {hours.map((hour) => (
                                    <Box
                                        key={hour}
                                        onClick={() => setTempHour(hour)}
                                        sx={{
                                            p: 1.25,
                                            cursor: "pointer",
                                            textAlign: "center",
                                            fontWeight: tempHour === hour ? 700 : 500,
                                            fontSize: "0.95rem",
                                            backgroundColor:
                                                tempHour === hour
                                                    ? theme.palette.primary.main
                                                    : "transparent",
                                            color:
                                                tempHour === hour
                                                    ? theme.palette.primary.contrastText
                                                    : theme.palette.text.primary,
                                            "&:hover": {
                                                backgroundColor:
                                                    tempHour === hour
                                                        ? theme.palette.primary.dark
                                                        : alpha(theme.palette.primary.main, 0.1),
                                            },
                                            transition: "all 0.2s ease",
                                            borderRadius: 0.5,
                                            m: 0.5,
                                        }}
                                    >
                                        {hour}
                                    </Box>
                                ))}
                            </Box>
                        </Grid>

                        {/* Minutes Column */}
                        <Grid item xs={6}>
                            <Typography 
                                variant="caption" 
                                sx={{ 
                                    mb: 1, 
                                    display: "block", 
                                    fontWeight: 600,
                                    color: "text.secondary",
                                }}
                            >
                                {isArabic ? "الدقيقة" : "Minute"}
                            </Typography>
                            <Box
                                sx={{
                                    maxHeight: 220,
                                    overflowY: "auto",
                                    border: `1px solid ${theme.palette.divider}`,
                                    borderRadius: 1.5,
                                    "&::-webkit-scrollbar": {
                                        width: "8px",
                                    },
                                    "&::-webkit-scrollbar-track": {
                                        background: alpha(theme.palette.divider, 0.1),
                                        borderRadius: "4px",
                                    },
                                    "&::-webkit-scrollbar-thumb": {
                                        backgroundColor: alpha(theme.palette.primary.main, 0.5),
                                        borderRadius: "4px",
                                        "&:hover": {
                                            backgroundColor: theme.palette.primary.main,
                                        },
                                    },
                                }}
                            >
                                {minutes.map((minute) => (
                                    <Box
                                        key={minute}
                                        onClick={() => setTempMinute(minute)}
                                        sx={{
                                            p: 1.25,
                                            cursor: "pointer",
                                            textAlign: "center",
                                            fontWeight: tempMinute === minute ? 700 : 500,
                                            fontSize: "0.95rem",
                                            backgroundColor:
                                                tempMinute === minute
                                                    ? theme.palette.primary.main
                                                    : "transparent",
                                            color:
                                                tempMinute === minute
                                                    ? theme.palette.primary.contrastText
                                                    : theme.palette.text.primary,
                                            "&:hover": {
                                                backgroundColor:
                                                    tempMinute === minute
                                                        ? theme.palette.primary.dark
                                                        : alpha(theme.palette.primary.main, 0.1),
                                            },
                                            transition: "all 0.2s ease",
                                            borderRadius: 0.5,
                                            m: 0.5,
                                        }}
                                    >
                                        {minute}
                                    </Box>
                                ))}
                            </Box>
                        </Grid>
                    </Grid>

                    {/* Preview & Buttons */}
                    <Box sx={{ mt: 2.5 }}>
                        <Box
                            sx={{
                                p: 1.5,
                                mb: 2,
                                textAlign: "center",
                                borderRadius: 1.5,
                                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                border: `2px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                            }}
                        >
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                                {isArabic ? "الوقت المحدد" : "Selected Time"}
                            </Typography>
                            <Typography variant="h6" fontWeight={700} color="primary.main">
                                {tempHour}:{tempMinute}
                            </Typography>
                        </Box>
                        <Box sx={{ display: "flex", gap: 1.5 }}>
                            <Button
                                fullWidth
                                variant="outlined"
                                onClick={handleClosePicker}
                                sx={{
                                    fontWeight: 600,
                                    textTransform: "none",
                                }}
                            >
                                {isArabic ? "إلغاء" : "Cancel"}
                            </Button>
                            <Button
                                fullWidth
                                variant="contained"
                                onClick={handleApply}
                                sx={{
                                    fontWeight: 600,
                                    textTransform: "none",
                                    boxShadow: 2,
                                }}
                            >
                                {isArabic ? "تطبيق" : "Apply"}
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </Popover>
        </>
    );
};

export default TimeInput;
